import { Tooltip } from '@/components/ui/tooltip';
import { chakra, HStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';

export interface SegmentedToggleOption<T extends string> {
  value: T;
  /** Visible text, or the accessible name and tooltip of an icon-only segment. */
  label: string;
  /** When true the segment is inert (muted, not selectable). */
  disabled?: boolean;
  /** Glyph drawn in `currentColor`; with `iconOnly` it replaces the label. */
  icon?: ReactNode;
  /** Colour of the selected segment's content (a platform logo in its brand colour). */
  activeColor?: string;
  /** Word badge after the label (e.g. « Coming soon » on a locked segment). */
  tag?: ReactNode;
  /** Explanation shown on hover or focus, readable on a disabled segment too. */
  tooltip?: string;
}

interface SegmentedToggleProps<T extends string> {
  /** Currently selected segment value. */
  value: T;
  onChange: (value: T) => void;
  /** Ordered segments. Two in practice, but any count renders. */
  options: SegmentedToggleOption<T>[];
  /** Radiogroup label for assistive tech. */
  ariaLabel: string;
  /** Per-segment box height (e.g. "7" or "30px"). */
  segmentHeight: string;
  /** Per-segment horizontal padding (e.g. "3" or "13px"). */
  segmentPx: string;
  /** Per-segment label font size (e.g. "xs" or "12px"). */
  fontSize: string;
  /** Track and segment corner radii; the drawer's square pills by default. */
  rounded?: { track: string; segment: string };
  /**
   * Draw each segment's `icon` alone, its `label` becoming the accessible name
   * and the native tooltip. Unselected glyphs are set grey, the selected one in
   * its `activeColor`.
   */
  iconOnly?: boolean;
}

/**
 * Generic two-state segmented control shared by the contact drawer's pill
 * toggles (profile IG/TikTok, affiliation Actif/En pause). Radiogroup semantics,
 * keyboard-operable. The active segment is differentiated by border + bg contrast
 * only — the mockup's segment box-shadow is dropped per house rules. Size knobs
 * (`segmentHeight`/`segmentPx`/`fontSize`) keep each caller's exact dimensions, so
 * both render identically to their prior hand-built versions. The track hugs its
 * segments (`fit-content`) rather than stretching across a block parent; the
 * header and manage-screen callers sit in flex rows, where that was already so.
 */
function SegmentedToggle<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  segmentHeight,
  segmentPx,
  fontSize,
  iconOnly = false,
  rounded = { track: 'md', segment: 'sm' },
}: SegmentedToggleProps<T>) {
  return (
    <HStack
      role="radiogroup"
      aria-label={ariaLabel}
      w="fit-content"
      gap="0.5"
      bg="whiteSmoke"
      rounded={rounded.track}
      p="1"
    >
      {options.map((option) => {
        const selected = value === option.value;
        const disabled = option.disabled ?? false;
        const idleColor = iconOnly ? 'primary.400' : 'primary.500';
        const segment = (
          <chakra.button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled || undefined}
            aria-label={iconOnly ? option.label : undefined}
            title={iconOnly ? option.label : undefined}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(option.value);
            }}
            h={segmentHeight}
            px={segmentPx}
            rounded={rounded.segment}
            fontFamily="heading"
            fontSize={fontSize}
            fontWeight="semibold"
            lineHeight="1"
            cursor={disabled ? 'not-allowed' : 'pointer'}
            bg={selected ? 'panelBg' : 'transparent'}
            display={iconOnly ? 'grid' : option.icon || option.tag ? 'inline-flex' : undefined}
            alignItems={iconOnly ? undefined : 'center'}
            gap={iconOnly ? undefined : '1.5'}
            placeItems={iconOnly ? 'center' : undefined}
            opacity={iconOnly && disabled ? 0.5 : undefined}
            color={disabled ? 'primary.300' : selected ? (option.activeColor ?? 'dark') : idleColor}
            borderWidth="1px"
            borderColor={selected ? 'border' : 'transparent'}
            transition="background 0.15s, color 0.15s, border-color 0.15s"
            _hover={selected || disabled ? undefined : { color: 'primary.600' }}
            _focusVisible={{
              outline: '2px solid',
              outlineColor: 'primary.900',
              outlineOffset: '1px',
            }}
          >
            {iconOnly ? (
              option.icon
            ) : (
              <>
                {option.icon}
                {option.label}
                {option.tag}
              </>
            )}
          </chakra.button>
        );
        if (!option.tooltip) return segment;
        // A disabled button fires no pointer events: the tooltip hangs on a wrapper.
        return (
          <Tooltip key={option.value} content={option.tooltip} positioning={{ placement: 'top' }}>
            <chakra.span display="inline-flex" tabIndex={disabled ? 0 : undefined}>
              {segment}
            </chakra.span>
          </Tooltip>
        );
      })}
    </HStack>
  );
}

export default SegmentedToggle;
